// src/pages/PermissionsSettings.js

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  FiBell,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiFileText,
  FiHome,
  FiKey,
  FiMapPin,
  FiPackage,
  FiSettings,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import {
  HiOutlineTruck,
} from "react-icons/hi2";

import api from "../axiosConfig";

import {
  getStoredUser,
} from "../lib/authStorage";

import {
  usePermissionsSettingsData,
} from "../hooks/usePermissionsSettingsData";

import "../styles/PermissionsSettings.css";

/* =========================================================
   Stored user
   ========================================================= */

const getCurrentRole =
  () => {
    const user =
      getStoredUser();

    return String(
      user?.role ||
      user?.Role ||
      ""
    )
      .trim()
      .toLowerCase();
  };

/* =========================================================
   Roles
   ========================================================= */

const ROLE_OPTIONS = [
  {
    value:
      "admin",
    label:
      "Admin",
  },
  {
    value:
      "manager",
    label:
      "Manager",
  },
  {
    value:
      "dispatcher",
    label:
      "Dispatcher",
  },
  {
    value:
      "accountant",
    label:
      "Accountant",
  },
];

const ACTIVE_PERM_TAB_KEY =
  "permissions.activeRoleTab";

/* =========================================================
   Sidebar permissions
   ========================================================= */

const SIDEBAR_ITEMS = [
  {
    key:
      "loads",
    i18n:
      "groups.sidebar.loads",
    icon:
      FiPackage,
  },
  {
    key:
      "vehicles",
    i18n:
      "groups.sidebar.vehicles",
    icon:
      HiOutlineTruck,
  },
  {
    key:
      "clients",
    i18n:
      "groups.sidebar.clients",
    icon:
      FiUsers,
  },
  {
    key:
      "places",
    i18n:
      "groups.sidebar.places",
    icon:
      FiMapPin,
  },
  {
    key:
      "invoices",
    i18n:
      "groups.sidebar.invoices",
    icon:
      FiFileText,
  },
  {
    key:
      "notifications",
    i18n:
      "groups.sidebar.notifications",
    icon:
      FiBell,
  },
];

/* =========================================================
   Settings permissions
   ========================================================= */

const SETTINGS_ITEMS = [
  {
    key:
      "generalSettings",
    i18n:
      "groups.settings.general_settings",
    icon:
      FiSettings,
  },
  {
    key:
      "companySettings",
    i18n:
      "groups.settings.company_settings",
    icon:
      FiHome,
  },
  {
    key:
      "profileSettings",
    i18n:
      "groups.settings.profile_settings",
    icon:
      FiUser,
  },
  {
    key:
      "usersSettings",
    i18n:
      "groups.settings.users_settings",
    icon:
      FiUsers,
  },
  {
    key:
      "permissionsSettings",
    i18n:
      "groups.settings.permissions_settings",
    icon:
      FiKey,
  },
  {
    key:
      "plansSubscriptionSettings",
    i18n:
      "groups.settings.plans_subscription_settings",
    icon:
      FiCreditCard,
  },
  {
    key:
      "invoicesStatementsSettings",
    i18n:
      "groups.settings.invoices_statements_settings",
    icon:
      FiFileText,
  },
];

/* =========================================================
   Personnel permissions
   ========================================================= */

/*
 * IMPORTANT:
 *
 * The database keeps all 16 granular Personnel permissions.
 *
 * The UI deliberately groups related write permissions into
 * simpler operational controls.
 *
 * Example:
 *
 * "Manage requests"
 * =
 * create
 * edit
 * cancel
 * delete
 *
 * Backend security still checks the individual permissions.
 */

const PERSONNEL_ITEMS = [
  {
    id:
      "calendarView",

    i18n:
      "groups.personnel.calendar_view",

    icon:
      FiCalendar,

    keys: [
      "personnelCalendarView",
    ],

    disableDependents: [
      "personnelWorkDaysManage",
    ],
  },

  {
    id:
      "workDaysManage",

    i18n:
      "groups.personnel.work_days_manage",

    icon:
      FiCalendar,

    keys: [
      "personnelWorkDaysManage",
    ],

    requiresView:
      "personnelCalendarView",
  },

  {
    id:
      "requestsView",

    i18n:
      "groups.personnel.requests_view",

    icon:
      FiUsers,

    keys: [
      "personnelRequestsView",
    ],

    disableDependents: [
      "personnelRequestsCreate",
      "personnelRequestsEdit",
      "personnelRequestsApprove",
      "personnelRequestsCancel",
      "personnelRequestsDelete",
    ],
  },

  {
    id:
      "requestsManage",

    i18n:
      "groups.personnel.requests_manage",

    icon:
      FiUsers,

    keys: [
      "personnelRequestsCreate",
      "personnelRequestsEdit",
      "personnelRequestsCancel",
      "personnelRequestsDelete",
    ],

    requiresView:
      "personnelRequestsView",
  },

  {
    id:
      "requestsApprove",

    i18n:
      "groups.personnel.requests_approve",

    icon:
      FiCheckCircle,

    keys: [
      "personnelRequestsApprove",
    ],

    requiresView:
      "personnelRequestsView",
  },

  {
    id:
      "publicHolidaysView",

    i18n:
      "groups.personnel.public_holidays_view",

    icon:
      FiFileText,

    keys: [
      "personnelPublicHolidaysView",
    ],

    disableDependents: [
      "personnelPublicHolidaysCreate",
      "personnelPublicHolidaysEdit",
      "personnelPublicHolidaysDelete",
    ],
  },

  {
    id:
      "publicHolidaysManage",

    i18n:
      "groups.personnel.public_holidays_manage",

    icon:
      FiFileText,

    keys: [
      "personnelPublicHolidaysCreate",
      "personnelPublicHolidaysEdit",
      "personnelPublicHolidaysDelete",
    ],

    requiresView:
      "personnelPublicHolidaysView",
  },

  {
    id:
      "compensationView",

    i18n:
      "groups.personnel.compensation_view",

    icon:
      FiCreditCard,

    keys: [
      "personnelCompensationView",
    ],

    disableDependents: [
      "personnelCompensationCreate",
      "personnelCompensationEdit",
      "personnelCompensationConfirm",
      "personnelCompensationDelete",
    ],
  },

  {
    id:
      "compensationManage",

    i18n:
      "groups.personnel.compensation_manage",

    icon:
      FiCreditCard,

    keys: [
      "personnelCompensationCreate",
      "personnelCompensationEdit",
      "personnelCompensationDelete",
    ],

    requiresView:
      "personnelCompensationView",
  },

  {
    id:
      "compensationConfirm",

    i18n:
      "groups.personnel.compensation_confirm",

    icon:
      FiCheckCircle,

    keys: [
      "personnelCompensationConfirm",
    ],

    requiresView:
      "personnelCompensationView",
  },
];

/* =========================================================
   Permission key helpers
   ========================================================= */

const ALL_PERMISSION_KEYS =
  Array.from(
    new Set([
      ...SIDEBAR_ITEMS.map(
        (
          item
        ) =>
          item.key
      ),

      ...SETTINGS_ITEMS.map(
        (
          item
        ) =>
          item.key
      ),

      ...PERSONNEL_ITEMS.flatMap(
        (
          item
        ) =>
          item.keys
      ),
    ])
  );

const TOTAL_VISIBLE_ITEMS =
  SIDEBAR_ITEMS.length +
  SETTINGS_ITEMS.length +
  PERSONNEL_ITEMS.length;

/* =========================================================
   Page
   ========================================================= */

export default function PermissionsSettings() {
  const {
    t,
  } =
    useTranslation(
      "permissions_settings"
    );

  const [
    role,
    setRole,
  ] = useState(
    () =>
      localStorage.getItem(
        ACTIVE_PERM_TAB_KEY
      ) ||
      "admin"
  );

  const {
    matrix,
    loading,

    refresh:
      refreshMatrix,

    patchRole,
  } =
    usePermissionsSettingsData();

  const [
    currentUserRole,
  ] =
    useState(
      () =>
        getCurrentRole()
    );

  const canEdit =
    currentUserRole ===
      "admin" ||
    Boolean(
      matrix?.[
        currentUserRole
      ]?.permissionsSettings
    );

  const putTimers =
    useRef(
      new Map()
    );

  const initialRefreshDoneRef =
    useRef(
      false
    );

  /* =========================================================
     Initial authoritative refresh
     ========================================================= */

  useEffect(
    () => {
      if (
        initialRefreshDoneRef.current
      ) {
        return;
      }

      initialRefreshDoneRef.current =
        true;

      refreshMatrix()
        .catch(
          (
            error
          ) => {
            console.warn(
              "[PermissionsSettings] initial refresh failed:",
              error
            );
          }
        );
    },
    [
      refreshMatrix,
    ]
  );

  /* =========================================================
     Clear pending saves
     ========================================================= */

  useEffect(
    () => {
      const timers =
        putTimers.current;

      return () => {
        timers.forEach(
          (
            timer
          ) => {
            window.clearTimeout(
              timer
            );
          }
        );

        timers.clear();
      };
    },
    []
  );

  /* =========================================================
     Persist selected role
     ========================================================= */

  useEffect(
    () => {
      localStorage.setItem(
        ACTIVE_PERM_TAB_KEY,
        role
      );
    },
    [
      role,
    ]
  );

  /* =========================================================
     Selected role index
     ========================================================= */

  const index =
    useMemo(
      () =>
        Math.max(
          0,
          ROLE_OPTIONS.findIndex(
            (
              option
            ) =>
              option.value ===
              role
          )
        ),
      [
        role,
      ]
    );

  /* =========================================================
     Save
     ========================================================= */

  const scheduleRoleSave =
    (
      targetRole,
      nextPermissions
    ) => {
      if (
        !canEdit
      ) {
        return;
      }

      const existingTimer =
        putTimers.current.get(
          targetRole
        );

      if (
        existingTimer
      ) {
        window.clearTimeout(
          existingTimer
        );
      }

      const timer =
        window.setTimeout(
          async () => {
            putTimers.current.delete(
              targetRole
            );

            try {
              await api.put(
                "/permissions/matrix",
                {
                  matrix: {
                    [
                      targetRole
                    ]:
                      nextPermissions,
                  },
                }
              );
            } catch (
              error
            ) {
              const status =
                error
                  ?.response
                  ?.status;

              /*
               * Optimistic update failed.
               * Restore authoritative state.
               */
              try {
                await refreshMatrix();
              } catch {
                // Keep current UI if recovery also fails.
              }

              if (
                status ===
                  403
              ) {
                alert(
                  t(
                    "alerts.forbidden_edit"
                  )
                );
              } else {
                alert(
                  t(
                    "alerts.save_failed"
                  )
                );
              }
            }
          },
          250
        );

      putTimers.current.set(
        targetRole,
        timer
      );
    };

  /* =========================================================
     Loading
     ========================================================= */

  if (
    loading ||
    !matrix
  ) {
    return (
      <div className="perm-settings">
        <div className="perm-header">
          <h1>
            {t(
              "title"
            )}
          </h1>

          <p>
            {t(
              "status.loading"
            )}
          </p>
        </div>
      </div>
    );
  }

  /* =========================================================
     Current role permissions
     ========================================================= */

  const rolePerms =
    matrix[
      role
    ] ||
    {};

  /* =========================================================
     Generic permission toggle
     ========================================================= */

  const toggleOne =
    (
      key
    ) => {
      if (
        !canEdit
      ) {
        return;
      }

      const currentRolePermissions =
        matrix?.[
          role
        ] ||
        {};

      const nextRolePermissions = {
        ...currentRolePermissions,

        [
          key
        ]:
          !currentRolePermissions[
            key
          ],
      };

      patchRole(
        role,
        nextRolePermissions
      );

      scheduleRoleSave(
        role,
        nextRolePermissions
      );
    };

  /* =========================================================
     Personnel grouped toggle
     ========================================================= */

  const isPersonnelItemEnabled =
    (
      item
    ) =>
      item.keys.every(
        (
          key
        ) =>
          Boolean(
            rolePerms[
              key
            ]
          )
      );

  const togglePersonnelItem =
    (
      item
    ) => {
      if (
        !canEdit
      ) {
        return;
      }

      const currentRolePermissions =
        matrix?.[
          role
        ] ||
        {};

      const currentlyEnabled =
        item.keys.every(
          (
            key
          ) =>
            Boolean(
              currentRolePermissions[
                key
              ]
            )
        );

      const nextValue =
        !currentlyEnabled;

      const nextRolePermissions = {
        ...currentRolePermissions,
      };

      item.keys.forEach(
        (
          key
        ) => {
          nextRolePermissions[
            key
          ] =
            nextValue;
        }
      );

      /*
       * Enabling an action automatically grants
       * permission to view that Personnel area.
       */
      if (
        nextValue &&
        item.requiresView
      ) {
        nextRolePermissions[
          item.requiresView
        ] =
          true;
      }

      /*
       * Disabling "View" automatically removes
       * actions that would otherwise become
       * inaccessible/incoherent.
       */
      if (
        !nextValue &&
        Array.isArray(
          item.disableDependents
        )
      ) {
        item.disableDependents.forEach(
          (
            key
          ) => {
            nextRolePermissions[
              key
            ] =
              false;
          }
        );
      }

      patchRole(
        role,
        nextRolePermissions
      );

      scheduleRoleSave(
        role,
        nextRolePermissions
      );
    };

  /* =========================================================
     Enable / disable everything
     ========================================================= */

  const setAll =
    (
      value
    ) => {
      if (
        !canEdit
      ) {
        return;
      }

      const next = {
        ...(
          matrix?.[
            role
          ] ||
          {}
        ),
      };

      ALL_PERMISSION_KEYS.forEach(
        (
          key
        ) => {
          next[
            key
          ] =
            Boolean(
              value
            );
        }
      );

      patchRole(
        role,
        next
      );

      scheduleRoleSave(
        role,
        next
      );
    };

  /* =========================================================
     Counters
     ========================================================= */

  const countOn =
    (
      items
    ) =>
      items.reduce(
        (
          count,
          item
        ) =>
          count +
          (
            rolePerms[
              item.key
            ]
              ? 1
              : 0
          ),
        0
      );

  const sidebarOn =
    countOn(
      SIDEBAR_ITEMS
    );

  const settingsOn =
    countOn(
      SETTINGS_ITEMS
    );

  const personnelOn =
    PERSONNEL_ITEMS.reduce(
      (
        count,
        item
      ) =>
        count +
        (
          isPersonnelItemEnabled(
            item
          )
            ? 1
            : 0
        ),
      0
    );

  const enabledCount =
    sidebarOn +
    settingsOn +
    personnelOn;

  /* =========================================================
     Render
     ========================================================= */

  return (
    <div className="perm-settings">
      <div className="perm-header">
        <h1>
          {t(
            "title"
          )}
        </h1>

        <p>
          {t(
            "subtitle"
          )}
        </p>
      </div>

      {/* =====================================================
          Role segmented toggle
          ===================================================== */}

      <div className="perm-toggle-wrap">
        <div
          className="perm-seg"
          style={{
            [
              "--count"
            ]:
              ROLE_OPTIONS.length,

            [
              "--i"
            ]:
              index,
          }}
          role="tablist"
          aria-label={t(
            "aria.roles"
          )}
          onKeyDown={(
            event
          ) => {
            if (
              event.key ===
              "ArrowRight"
            ) {
              const next =
                (
                  index +
                  1
                ) %
                ROLE_OPTIONS.length;

              setRole(
                ROLE_OPTIONS[
                  next
                ].value
              );
            } else if (
              event.key ===
              "ArrowLeft"
            ) {
              const previous =
                (
                  index -
                  1 +
                  ROLE_OPTIONS.length
                ) %
                ROLE_OPTIONS.length;

              setRole(
                ROLE_OPTIONS[
                  previous
                ].value
              );
            }
          }}
        >
          <div
            className="perm-seg-indicator"
            aria-hidden="true"
          />

          {ROLE_OPTIONS.map(
            (
              option,
              optionIndex
            ) => (
              <button
                key={
                  option.value
                }
                type="button"
                className={`perm-seg-btn ${
                  optionIndex ===
                  index
                    ? "is-active"
                    : ""
                }`}
                onClick={() =>
                  setRole(
                    option.value
                  )
                }
                role="tab"
                aria-selected={
                  optionIndex ===
                  index
                }
                tabIndex={
                  optionIndex ===
                  index
                    ? 0
                    : -1
                }
              >
                {t(
                  `roles.${option.value}`,
                  option.label
                )}
              </button>
            )
          )}
        </div>
      </div>

      {/* =====================================================
          Toolbar
          ===================================================== */}

      <div className="perm-tools">
        <div className="perm-tools-left">
          <span className="perm-counter">
            {t(
              "tools.enabled_counter",
              {
                count:
                  enabledCount,

                total:
                  TOTAL_VISIBLE_ITEMS,
              }
            )}
          </span>
        </div>

        <div className="perm-tools-right">
          <button
            type="button"
            className="perm-btn ghost"
            onClick={() =>
              setAll(
                false
              )
            }
            disabled={
              !canEdit
            }
          >
            {t(
              "tools.disable_all"
            )}
          </button>

          <button
            type="button"
            className="perm-btn ghost"
            onClick={() =>
              setAll(
                true
              )
            }
            disabled={
              !canEdit
            }
          >
            {t(
              "tools.enable_all"
            )}
          </button>
        </div>
      </div>

      {/* =====================================================
          Main permission columns
          ===================================================== */}

      <div className="perm-columns">
        {/* Sidebar permissions */}

        <div className="perm-col">
          <div className="perm-col-head">
            <h3 className="perm-section-title">
              {t(
                "sections.sidebar.title"
              )}
            </h3>

            <span className="perm-section-counter">
              {t(
                "sections.counter",
                {
                  count:
                    sidebarOn,

                  total:
                    SIDEBAR_ITEMS.length,
                }
              )}
            </span>
          </div>

          <ul
            className="perm-list"
            role="list"
          >
            {SIDEBAR_ITEMS.map(
              (
                item
              ) => {
                const on =
                  Boolean(
                    rolePerms[
                      item.key
                    ]
                  );

                const label =
                  t(
                    item.i18n
                  );

                const Icon =
                  item.icon;

                return (
                  <li
                    key={
                      item.key
                    }
                    className="perm-row"
                  >
                    <div className="perm-row-left">
                      <Icon
                        className="perm-item-icon"
                        aria-hidden="true"
                      />

                      <span className="perm-label">
                        {
                          label
                        }
                      </span>
                    </div>

                    <button
                      type="button"
                      className={`perm-switch ${
                        on
                          ? "is-on"
                          : ""
                      }`}
                      role="switch"
                      aria-checked={
                        on
                      }
                      aria-label={t(
                        "aria.toggle_access",
                        {
                          label,
                        }
                      )}
                      onClick={() =>
                        toggleOne(
                          item.key
                        )
                      }
                      disabled={
                        !canEdit
                      }
                      data-readonly={
                        !canEdit
                          ? "true"
                          : undefined
                      }
                    >
                      <span
                        className="perm-switch-track"
                        aria-hidden="true"
                      />

                      <span
                        className="perm-switch-dot"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                );
              }
            )}
          </ul>
        </div>

        {/* Settings permissions */}

        <div className="perm-col">
          <div className="perm-col-head">
            <h3 className="perm-section-title">
              {t(
                "sections.settings.title"
              )}
            </h3>

            <span className="perm-section-counter">
              {t(
                "sections.counter",
                {
                  count:
                    settingsOn,

                  total:
                    SETTINGS_ITEMS.length,
                }
              )}
            </span>
          </div>

          <ul
            className="perm-list"
            role="list"
          >
            {SETTINGS_ITEMS.map(
              (
                item
              ) => {
                const on =
                  Boolean(
                    rolePerms[
                      item.key
                    ]
                  );

                const label =
                  t(
                    item.i18n
                  );

                const Icon =
                  item.icon;

                return (
                  <li
                    key={
                      item.key
                    }
                    className="perm-row"
                  >
                    <div className="perm-row-left">
                      <Icon
                        className="perm-item-icon"
                        aria-hidden="true"
                      />

                      <span className="perm-label">
                        {
                          label
                        }
                      </span>
                    </div>

                    <button
                      type="button"
                      className={`perm-switch ${
                        on
                          ? "is-on"
                          : ""
                      }`}
                      role="switch"
                      aria-checked={
                        on
                      }
                      aria-label={t(
                        "aria.toggle_access",
                        {
                          label,
                        }
                      )}
                      onClick={() =>
                        toggleOne(
                          item.key
                        )
                      }
                      disabled={
                        !canEdit
                      }
                      data-readonly={
                        !canEdit
                          ? "true"
                          : undefined
                      }
                    >
                      <span
                        className="perm-switch-track"
                        aria-hidden="true"
                      />

                      <span
                        className="perm-switch-dot"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                );
              }
            )}
          </ul>
        </div>
      </div>

      {/* =====================================================
          Personnel permissions
          ===================================================== */}

      <div
        className="perm-col"
        style={{
          marginTop:
            "16px",
        }}
      >
        <div className="perm-col-head">
          <h3 className="perm-section-title">
            {t(
              "sections.personnel.title"
            )}
          </h3>

          <span className="perm-section-counter">
            {t(
              "sections.counter",
              {
                count:
                  personnelOn,

                total:
                  PERSONNEL_ITEMS.length,
              }
            )}
          </span>
        </div>

        <ul
          className="perm-list"
          role="list"
          style={{
            display:
              "grid",

            gridTemplateColumns:
              "repeat(auto-fit, minmax(280px, 1fr))",

            gap:
              "10px",
          }}
        >
          {PERSONNEL_ITEMS.map(
            (
              item
            ) => {
              const on =
                isPersonnelItemEnabled(
                  item
                );

              const label =
                t(
                  item.i18n
                );

              const Icon =
                item.icon;

              return (
                <li
                  key={
                    item.id
                  }
                  className="perm-row"
                >
                  <div className="perm-row-left">
                    <Icon
                      className="perm-item-icon"
                      aria-hidden="true"
                    />

                    <span className="perm-label">
                      {
                        label
                      }
                    </span>
                  </div>

                  <button
                    type="button"
                    className={`perm-switch ${
                      on
                        ? "is-on"
                        : ""
                    }`}
                    role="switch"
                    aria-checked={
                      on
                    }
                    aria-label={t(
                      "aria.toggle_access",
                      {
                        label,
                      }
                    )}
                    onClick={() =>
                      togglePersonnelItem(
                        item
                      )
                    }
                    disabled={
                      !canEdit
                    }
                    data-readonly={
                      !canEdit
                        ? "true"
                        : undefined
                    }
                  >
                    <span
                      className="perm-switch-track"
                      aria-hidden="true"
                    />

                    <span
                      className="perm-switch-dot"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              );
            }
          )}
        </ul>
      </div>
    </div>
  );
}